import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import PageContainer from '../../../components/Redesing/page-container';
import { Chart } from '../../../components/Redesing/chart';
import {
  ChatMetrics,
  ChatSystemOption,
  fetchChatMetrics,
  fetchChatSystems,
} from '../../../hooks/chat/api';
import { tagCategory, tagLabel, useChatTagGlossary } from '../tagGlossary';
import {
  RANGE_DAYS,
  RangeDays,
  closedShare,
  dayLabel,
  fillDays,
  formatCsat,
  formatDuration,
  handlerLabel,
  metricsRange,
  shareOf,
} from '../metricsFormat';

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : String(error ?? '');

function MetricCard({
  label,
  value,
  detail,
  hint,
}: {
  label: string;
  value: string;
  detail?: string;
  hint?: string;
}) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }} data-testid="metric-card">
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Tooltip title={hint ?? ''} placement="top-start">
          <Typography variant="overline" color="text.secondary">
            {label}
          </Typography>
        </Tooltip>
        <Typography variant="h4" sx={{ mt: 0.5 }}>
          {value}
        </Typography>
        {detail && (
          <Typography variant="body2" color="text.secondary">
            {detail}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

interface CountRow {
  name: string;
  count: number;
}

/** Rows under a heading row (a glossary category; "Not in glossary" for old strings). */
interface CountGroup {
  title: string;
  rows: CountRow[];
}

function CountTable({
  title,
  rows,
  groups,
  total,
  subheader,
  empty,
  nameHeader,
}: {
  title: string;
  /** Flat rows, or `groups` for rows under heading rows. */
  rows?: CountRow[];
  groups?: CountGroup[];
  /**
   * What Share divides by. Left out, there is no Share column: tags on
   * sessions do not add up to the sessions (most have none, some have
   * several), so a share of all sessions reads as nonsense.
   */
  total?: number;
  /** A line under the title. */
  subheader?: string;
  empty: string;
  nameHeader: string;
}) {
  const showShare = total !== undefined;
  const flat = rows ?? [];
  const grouped = groups ?? [];
  const isEmpty = flat.length === 0 && grouped.every((g) => g.rows.length === 0);
  const renderRow = (row: CountRow, key: string) => (
    <TableRow key={key}>
      <TableCell>{row.name}</TableCell>
      <TableCell align="right">{row.count}</TableCell>
      {showShare && <TableCell align="right">{shareOf(row.count, total)}</TableCell>}
    </TableRow>
  );
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardHeader
        title={title}
        subheader={subheader}
        titleTypographyProps={{ variant: 'subtitle1' }}
        subheaderTypographyProps={{ variant: 'caption' }}
      />
      {isEmpty ? (
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            {empty}
          </Typography>
        </CardContent>
      ) : (
        <Table size="small" aria-label={title}>
          <TableHead>
            <TableRow>
              <TableCell>{nameHeader}</TableCell>
              <TableCell align="right">Sessions</TableCell>
              {showShare && <TableCell align="right">Share</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {flat.map((row) => renderRow(row, row.name))}
            {grouped.map((group) => [
              <TableRow key={`group-${group.title}`} data-testid="count-group">
                <TableCell
                  colSpan={showShare ? 3 : 2}
                  sx={{ bgcolor: 'action.hover', typography: 'caption', fontWeight: 600 }}
                >
                  {group.title}
                </TableCell>
              </TableRow>,
              ...group.rows.map((row) => renderRow(row, `${group.title}-${row.name}`)),
            ])}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

function SessionsByDayChart({
  days,
}: {
  days: Array<{ day: string; sessions: number }>;
}) {
  const theme = useTheme();
  const options = useMemo(
    () => ({
      chart: {
        background: 'transparent',
        toolbar: { show: false },
        animations: { enabled: false },
      },
      colors: [theme.palette.primary.main],
      dataLabels: { enabled: false },
      grid: { borderColor: theme.palette.divider, strokeDashArray: 2 },
      plotOptions: { bar: { borderRadius: 3, columnWidth: '60%' } },
      theme: { mode: theme.palette.mode },
      xaxis: {
        categories: days.map((d) => dayLabel(d.day)),
        labels: { rotate: -45, hideOverlappingLabels: true },
        axisBorder: { color: theme.palette.divider },
        axisTicks: { show: false },
      },
      yaxis: { labels: { formatter: (v: number) => `${Math.round(v)}` } },
      tooltip: { y: { formatter: (v: number) => `${v} session${v === 1 ? '' : 's'}` } },
    }),
    [days, theme]
  );
  const series = useMemo(
    () => [{ name: 'Sessions', data: days.map((d) => d.sessions) }],
    [days]
  );
  return <Chart type="bar" height={260} options={options} series={series} />;
}

/**
 * Customer Support's numbers from ant's chatMetrics: sessions opened in the
 * range (UTC days), how fast Support answered and resolved them, the
 * riders' ratings, and the split by tag and by who handled them. Global,
 * like /support: ant scopes the numbers by the admin's token.
 */
export default function SupportMetricsPage() {
  const [days, setDays] = useState<RangeDays>(30);
  const [systemId, setSystemId] = useState('');
  const [systems, setSystems] = useState<ChatSystemOption[]>([]);
  const [metrics, setMetrics] = useState<ChatMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestGeneration = useRef(0);
  // For display only: labels and category headings; the counts are ant's.
  const { glossary } = useChatTagGlossary();

  useEffect(() => {
    document.title = 'Support metrics';
    fetchChatSystems()
      .then(setSystems)
      .catch(() => setSystems([]));
  }, []);

  const load = useCallback(async () => {
    const generation = ++requestGeneration.current;
    const { from, to } = metricsRange(days);
    setLoading(true);
    setError(null);
    try {
      const result = await fetchChatMetrics(from, to, systemId || null);
      if (generation === requestGeneration.current) {
        setMetrics(result);
        setError(null);
      }
    } catch (e) {
      if (generation === requestGeneration.current) setError(errorText(e));
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, [days, systemId]);

  useEffect(() => {
    // Results from the previous filters do not belong under the new labels.
    setMetrics(null);
    load();
    return () => {
      requestGeneration.current += 1;
    };
  }, [load]);

  const chartDays = useMemo(
    () => (metrics ? fillDays(metrics.byDay, metrics.from, metrics.to) : []),
    [metrics]
  );
  const csat = metrics ? formatCsat(metrics.csatAverage, metrics.csatCount) : null;

  // ant's byTag, one heading per glossary category in sheet order, old
  // strings (and ids the glossary does not know) under "Not in glossary".
  const tagGroups = useMemo<CountGroup[]>(() => {
    if (!metrics) return [];
    const byCategory = new Map<string, CountGroup & { position: number }>();
    const others: CountRow[] = [];
    metrics.byTag.forEach((t) => {
      const row = { name: tagLabel(glossary, t.tag), count: t.count };
      const category = tagCategory(glossary, t.tag);
      if (!category) {
        others.push(row);
        return;
      }
      const group = byCategory.get(category.id) ?? {
        title: category.name,
        rows: [],
        position: category.position,
      };
      group.rows.push(row);
      byCategory.set(category.id, group);
    });
    const groups: CountGroup[] = Array.from(byCategory.values())
      .sort((a, b) => a.position - b.position)
      .map(({ title, rows }) => ({ title, rows }));
    if (others.length > 0) groups.push({ title: 'Not in glossary', rows: others });
    return groups;
  }, [metrics, glossary]);

  return (
    <PageContainer spaceName="Support metrics">
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          gap={2}
        >
          <Button
            component={RouterLink}
            to="/support"
            startIcon={<ArrowBackIcon />}
            sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
          >
            Support Chat
          </Button>
          <Button component={RouterLink} to="/support/issues" size="small">
            Issues by tag
          </Button>
          <Box sx={{ flex: 1 }} />
          <ToggleButtonGroup
            value={days}
            exclusive
            size="small"
            onChange={(_, v) => v && setDays(v)}
            aria-label="Range"
          >
            {RANGE_DAYS.map((d) => (
              <ToggleButton key={d} value={d} sx={{ textTransform: 'none', px: 1.5 }}>
                {d} days
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            select
            size="small"
            label="System"
            value={systemId}
            onChange={(e) => setSystemId(e.target.value)}
            sx={{ minWidth: 220 }}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            inputProps={{ 'data-testid': 'metrics-system' }}
          >
            <MenuItem value="">All systems</MenuItem>
            {systems.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name || 'Unnamed system'}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} disabled={loading} aria-label="Refresh metrics">
                {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {error && <Alert severity="error">Could not load the metrics. {error}</Alert>}
        {metrics?.truncated && (
          <Alert severity="warning">
            This range holds more sessions than one read covers. The numbers cover the
            earliest ones; pick a shorter range for the rest.
          </Alert>
        )}

        {!metrics && loading && (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        )}

        {metrics && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={6} md={4} lg={2}>
                <MetricCard
                  label="Sessions"
                  value={String(metrics.sessions)}
                  hint="Chats opened in the range"
                />
              </Grid>
              <Grid item xs={6} md={4} lg={2}>
                <MetricCard
                  label="Closed"
                  value={String(metrics.closed)}
                  detail={`${closedShare(metrics.sessions, metrics.closed)} of sessions`}
                />
              </Grid>
              <Grid item xs={6} md={4} lg={2}>
                <MetricCard
                  label="First response"
                  value={formatDuration(metrics.medianFirstResponseSeconds)}
                  detail="median"
                  hint="From the rider's first message to Support's first answer"
                />
              </Grid>
              <Grid item xs={6} md={4} lg={2}>
                <MetricCard
                  label="First response p90"
                  value={formatDuration(metrics.p90FirstResponseSeconds)}
                  detail="9 in 10 answered within"
                />
              </Grid>
              <Grid item xs={6} md={4} lg={2}>
                <MetricCard
                  label="Resolution"
                  value={formatDuration(metrics.medianResolutionSeconds)}
                  detail="median, open to closed"
                />
              </Grid>
              <Grid item xs={6} md={4} lg={2}>
                <MetricCard
                  label="CSAT"
                  value={csat?.value ?? '—'}
                  detail={
                    csat && csat.value !== '—' ? `of 5 · ${csat.detail}` : csat?.detail
                  }
                  hint="The riders' ratings of closed chats, 1 to 5"
                />
              </Grid>
            </Grid>

            <Card variant="outlined">
              <CardHeader
                title="Sessions by day"
                subheader="UTC days"
                titleTypographyProps={{ variant: 'subtitle1' }}
              />
              <CardContent sx={{ pt: 0 }}>
                {metrics.sessions === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No sessions in this range.
                  </Typography>
                ) : (
                  <SessionsByDayChart days={chartDays} />
                )}
              </CardContent>
            </Card>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CountTable
                  title="Tagged by CS"
                  subheader={`Out of ${metrics.sessions} sessions in range. A session can carry several tags or none.`}
                  nameHeader="Tag"
                  empty="No tagged sessions."
                  groups={tagGroups}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CountTable
                  title="By handler"
                  nameHeader="Handled by"
                  total={metrics.sessions}
                  empty="No sessions."
                  rows={metrics.byHandler.map((h) => ({
                    name: handlerLabel(h.handledBy),
                    count: h.count,
                  }))}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Stack>
    </PageContainer>
  );
}
